import React, {
  FormEventHandler,
  HTMLInputTypeAttribute,
  KeyboardEventHandler,
  MutableRefObject,
  forwardRef,
} from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';
import cn from 'classnames';
import styles from './Input.module.scss';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';

export interface InputProps {
  placeholder?: string;
  name?: string;
  register?: UseFormRegisterReturn<string> | null;
  errorMessage?: string | null;
  classname?: string;
  type?: HTMLInputTypeAttribute;
  children?: React.ReactElement;
  isRequired?: boolean;
  readOnly?: boolean;
  onChange?: (data: any) => any;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  onFocus?: FormEventHandler<HTMLInputElement>;
  onClick?: (data?: any) => any;
  onInput?: FormEventHandler<HTMLInputElement>;
  onInputCapture?: FormEventHandler<HTMLInputElement>;
  onKeyDownCapture?: KeyboardEventHandler<HTMLInputElement>;
  tabIndex?: number;
  defaultValue?: any;
  label?: string;
  pattern?: string;
  inputStyle?: 'clasic' | 'compressed' | 'ultra-compressed';
}

export const Input = forwardRef(
  (
    {
      placeholder,
      name,
      register,
      errorMessage,
      classname,
      type,
      children,
      isRequired,
      readOnly,
      onChange = () => {},
      onClick,
      onKeyDown,
      onFocus,
      onInputCapture,
      tabIndex,
      defaultValue,
      label,
      pattern,
      inputStyle = 'clasic',
      onInput,
      onKeyDownCapture = () => {},
    }: InputProps,
    ref: MutableRefObject<HTMLInputElement>
  ) => {
    return (
      <div
        className={cn(
          styles.root,
          errorMessage ? styles.error : undefined,
          readOnly && styles.readonly,
          styles[inputStyle],
          classname
        )}
      >
        {label && (
          <label htmlFor={name}>
            {label}
            {isRequired && <span className={styles.required}>*</span>}
          </label>
        )}
        <input
          id={name}
          type={type}
          placeholder={placeholder}
          readOnly={readOnly}
          contentEditable={!readOnly}
          onClick={onClick}
          tabIndex={tabIndex}
          defaultValue={defaultValue}
          onKeyDown={onKeyDown}
          ref={ref}
          onFocus={onFocus}
          {...register}
          pattern={pattern}
          onKeyDownCapture={onKeyDownCapture}
          onChange={(event) => {
            onChange?.(event);
            register?.onChange?.(event);
          }}
          onInput={onInput}
          onInputCapture={onInputCapture}
        />
        {children && children}
        {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
      </div>
    );
  }
);

Input.displayName = 'Input';

Input.defaultProps = {
  placeholder: 'value',
  classname: '',
  type: 'text',
  children: null,
  isRequired: false,
  readOnly: false,
  onChange: (data: any) => {},
  onKeyDown: (data: any) => {},
  onFocus: (data: any) => {},
  onClick: (data?: any) => {},
  tabIndex: 0,
  name: '',
  register: null,
  errorMessage: null,
  defaultValue: null,
};
